import React from 'react';
import { motion, AnimatePresence } from 'motion/react';

export interface DeckBuilderSettings {
  clickAction: 'add' | 'preview';
  gridSize: 'compact' | 'medium' | 'large';
  deckDisplayMode: 'list' | 'grid';
  showCardNames: boolean;
  showDeckQuantityBadge: boolean;
  showCostPills: boolean;
  deckSort: 'cost' | 'name' | 'type';
  confirmBeforeClear: boolean;
  enableAnimations: boolean;
}

export const DEFAULT_DECKBUILDER_SETTINGS: DeckBuilderSettings = {
  clickAction: 'add',
  gridSize: 'medium',
  deckDisplayMode: 'list',
  showCardNames: true,
  showDeckQuantityBadge: true,
  showCostPills: true,
  deckSort: 'cost',
  confirmBeforeClear: true,
  enableAnimations: true,
};

const STORAGE_KEY = 'cardumy_deckbuilder_settings';

export const loadDeckBuilderSettings = (): DeckBuilderSettings => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_DECKBUILDER_SETTINGS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_DECKBUILDER_SETTINGS, ...parsed };
  } catch {
    return DEFAULT_DECKBUILDER_SETTINGS;
  }
};

export const saveDeckBuilderSettings = (settings: DeckBuilderSettings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (err) {
    console.error('Failed to save deckbuilder settings:', err);
  }
};

interface DeckBuilderSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: DeckBuilderSettings;
  onUpdateSettings: (newSettings: DeckBuilderSettings) => void;
  activeGameName?: string;
}

export const DeckBuilderSettingsModal: React.FC<DeckBuilderSettingsModalProps> = ({
  isOpen,
  onClose,
  settings,
  onUpdateSettings,
  activeGameName = 'Card Game'
}) => {
  const updateSetting = <K extends keyof DeckBuilderSettings>(key: K, value: DeckBuilderSettings[K]) => {
    const updated = { ...settings, [key]: value };
    onUpdateSettings(updated);
    saveDeckBuilderSettings(updated);
  };

  const handleResetDefaults = () => {
    onUpdateSettings(DEFAULT_DECKBUILDER_SETTINGS);
    saveDeckBuilderSettings(DEFAULT_DECKBUILDER_SETTINGS);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div 
        id="deckbuilder-settings-backdrop"
        className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/90 overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <motion.div
          id="deckbuilder-settings-dialog"
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-xl bg-slate-900 border border-slate-800 rounded-3xl p-5 sm:p-7 shadow-2xl space-y-6 max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-white/5 pb-4 shrink-0">
            <div className="flex items-center space-x-3.5">
              <div className="w-11 h-11 rounded-2xl bg-purple-600/10 border border-purple-500/20 text-purple-400 flex items-center justify-center text-lg shrink-0 shadow-lg shadow-purple-900/20">
                <i className="fas fa-sliders-h"></i>
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-black text-white uppercase italic tracking-tight">
                  Configurações do Construtor
                </h3>
                <p className="text-[11px] text-slate-400 font-medium">
                  Personalize a experiência e exibição em {activeGameName}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-xl bg-slate-950/60 border border-slate-800 text-slate-400 hover:text-white hover:border-slate-700 flex items-center justify-center transition-colors"
              title="Fechar"
            >
              <i className="fas fa-times text-sm"></i>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto space-y-5 pr-1 scrollbar-thin scrollbar-thumb-slate-800">
            
            {/* Seção 1: Interação de Clique no Acervo */}
            <div className="space-y-2.5">
              <div className="flex items-center justify-between px-1">
                <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest flex items-center space-x-1.5">
                  <i className="fas fa-mouse-pointer text-[9px]"></i>
                  <span>Ação ao Clicar na Carta (Acervo)</span>
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={() => updateSetting('clickAction', 'add')}
                  className={`p-3 rounded-2xl border text-left transition-all relative ${
                    settings.clickAction === 'add'
                      ? 'bg-purple-600/15 border-purple-500/50 text-white shadow-lg shadow-purple-600/10'
                      : 'bg-slate-950/40 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-black text-white flex items-center space-x-1.5">
                      <i className="fas fa-plus text-purple-400 text-[10px]"></i>
                      <span>Adicionar Direto</span>
                    </span>
                    {settings.clickAction === 'add' && (
                      <span className="w-2 h-2 rounded-full bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.8)]"></span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400 leading-snug">
                    Clique esquerdo insere a carta no baralho. Botão direito abre detalhes.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => updateSetting('clickAction', 'preview')}
                  className={`p-3 rounded-2xl border text-left transition-all relative ${
                    settings.clickAction === 'preview'
                      ? 'bg-purple-600/15 border-purple-500/50 text-white shadow-lg shadow-purple-600/10'
                      : 'bg-slate-950/40 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-black text-white flex items-center space-x-1.5">
                      <i className="fas fa-eye text-purple-400 text-[10px]"></i>
                      <span>Abrir Detalhes</span>
                    </span>
                    {settings.clickAction === 'preview' && (
                      <span className="w-2 h-2 rounded-full bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.8)]"></span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400 leading-snug">
                    Clique esquerdo abre o modal completo de inspeção com botão de adicionar.
                  </p>
                </button>
              </div>
            </div>

            {/* Seção 2: Visualização do Grid de Cartas */}
            <div className="space-y-2.5">
              <div className="flex items-center justify-between px-1">
                <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest flex items-center space-x-1.5">
                  <i className="fas fa-th-large text-[9px]"></i>
                  <span>Tamanho do Grid do Acervo</span>
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'compact', label: 'Compacto', desc: 'Mais cartas', icon: 'fa-th' },
                  { id: 'medium', label: 'Normal', desc: 'Equilibrado', icon: 'fa-th-large' },
                  { id: 'large', label: 'Grande', desc: 'Arte em foco', icon: 'fa-square' },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => updateSetting('gridSize', item.id as any)}
                    className={`p-2.5 rounded-2xl border text-center transition-all flex flex-col items-center justify-center gap-1 ${
                      settings.gridSize === item.id
                        ? 'bg-purple-600/20 border-purple-500 text-white shadow-lg shadow-purple-600/10'
                        : 'bg-slate-950/40 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-300'
                    }`}
                  >
                    <i className={`fas ${item.icon} text-xs ${settings.gridSize === item.id ? 'text-purple-400' : 'text-slate-500'}`}></i>
                    <span className="text-[11px] font-black uppercase tracking-wider">{item.label}</span>
                    <span className="text-[9px] text-slate-500">{item.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Seção 3: Modo de Exibição do Baralho (Lista vs Grid) */}
            <div className="space-y-2.5">
              <div className="flex items-center justify-between px-1">
                <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest flex items-center space-x-1.5">
                  <i className="fas fa-layer-group text-[9px]"></i>
                  <span>Exibição do Baralho (Composição)</span>
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={() => updateSetting('deckDisplayMode', 'list')}
                  className={`p-3 rounded-2xl border text-left transition-all relative ${
                    settings.deckDisplayMode === 'list'
                      ? 'bg-purple-600/15 border-purple-500/50 text-white shadow-lg shadow-purple-600/10'
                      : 'bg-slate-950/40 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-black text-white flex items-center space-x-1.5">
                      <i className="fas fa-list text-purple-400 text-[10px]"></i>
                      <span>Modo Lista Compacta</span>
                    </span>
                    {settings.deckDisplayMode === 'list' && (
                      <span className="w-2 h-2 rounded-full bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.8)]"></span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400 leading-snug">
                    Exibe a lista clássica de linhas com nomes, custos numéricos e contadores.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => updateSetting('deckDisplayMode', 'grid')}
                  className={`p-3 rounded-2xl border text-left transition-all relative ${
                    settings.deckDisplayMode === 'grid'
                      ? 'bg-purple-600/15 border-purple-500/50 text-white shadow-lg shadow-purple-600/10'
                      : 'bg-slate-950/40 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-black text-white flex items-center space-x-1.5">
                      <i className="fas fa-th-large text-purple-400 text-[10px]"></i>
                      <span>Modo Grid Visual (Galeria)</span>
                    </span>
                    {settings.deckDisplayMode === 'grid' && (
                      <span className="w-2 h-2 rounded-full bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.8)]"></span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400 leading-snug">
                    Exibe as cartas do deck em formato de grade com imagens, badges e controles.
                  </p>
                </button>
              </div>
            </div>

            {/* Seção 4: Ordenação das Cartas no Baralho */}
            <div className="space-y-2.5">
              <div className="flex items-center justify-between px-1">
                <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest flex items-center space-x-1.5">
                  <i className="fas fa-sort-amount-down text-[9px]"></i>
                  <span>Ordenação na Lista do Baralho</span>
                </span>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'cost', label: 'Custo / Mana', icon: 'fa-bolt' },
                  { id: 'name', label: 'Nome (A-Z)', icon: 'fa-font' },
                  { id: 'type', label: 'Tipo / Rarity', icon: 'fa-layer-group' },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => updateSetting('deckSort', item.id as any)}
                    className={`p-2.5 rounded-2xl border text-center transition-all flex flex-col items-center justify-center gap-1 ${
                      settings.deckSort === item.id
                        ? 'bg-purple-600/20 border-purple-500 text-white shadow-lg shadow-purple-600/10'
                        : 'bg-slate-950/40 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-300'
                    }`}
                  >
                    <i className={`fas ${item.icon} text-xs ${settings.deckSort === item.id ? 'text-purple-400' : 'text-slate-500'}`}></i>
                    <span className="text-[10px] font-black uppercase tracking-wider">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Seção 5: Opções de Exibição & Toggles */}
            <div className="space-y-2">
              <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest px-1 flex items-center space-x-1.5">
                <i className="fas fa-toggle-on text-[9px]"></i>
                <span>Preferências de Exibição</span>
              </span>

              <div className="bg-slate-950/40 rounded-2xl border border-slate-800 divide-y divide-white/5 overflow-hidden">
                {/* Nome da carta */}
                <label className="flex items-center justify-between p-3.5 hover:bg-white/[0.02] cursor-pointer transition-colors">
                  <div className="space-y-0.5 pr-4">
                    <span className="text-xs font-bold text-white block">Exibir Nome da Carta no Acervo</span>
                    <span className="text-[11px] text-slate-400 block">Mostra a legenda com o título abaixo de cada imagem de carta.</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.showCardNames}
                    onChange={(e) => updateSetting('showCardNames', e.target.checked)}
                    className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-purple-600 focus:ring-purple-500 cursor-pointer"
                  />
                </label>

                {/* Badge de quantidade */}
                <label className="flex items-center justify-between p-3.5 hover:bg-white/[0.02] cursor-pointer transition-colors">
                  <div className="space-y-0.5 pr-4">
                    <span className="text-xs font-bold text-white block">Indicador de Cópias no Baralho</span>
                    <span className="text-[11px] text-slate-400 block">Exibe um selo roxo sobre as cartas que já estão inclusas no deck atual.</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.showDeckQuantityBadge}
                    onChange={(e) => updateSetting('showDeckQuantityBadge', e.target.checked)}
                    className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-purple-600 focus:ring-purple-500 cursor-pointer"
                  />
                </label>

                {/* Badge de custo */}
                <label className="flex items-center justify-between p-3.5 hover:bg-white/[0.02] cursor-pointer transition-colors">
                  <div className="space-y-0.5 pr-4">
                    <span className="text-xs font-bold text-white block">Pílulas de Custo / Energia na Lista</span>
                    <span className="text-[11px] text-slate-400 block">Mostra o valor numérico de custo destacado ao lado de cada carta na barra lateral.</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.showCostPills}
                    onChange={(e) => updateSetting('showCostPills', e.target.checked)}
                    className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-purple-600 focus:ring-purple-500 cursor-pointer"
                  />
                </label>

                {/* Confirmação de Limpar */}
                <label className="flex items-center justify-between p-3.5 hover:bg-white/[0.02] cursor-pointer transition-colors">
                  <div className="space-y-0.5 pr-4">
                    <span className="text-xs font-bold text-white block">Confirmar antes de Limpar Baralho</span>
                    <span className="text-[11px] text-slate-400 block">Solicita confirmação visual para evitar remoção acidental de todas as cartas.</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.confirmBeforeClear}
                    onChange={(e) => updateSetting('confirmBeforeClear', e.target.checked)}
                    className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-purple-600 focus:ring-purple-500 cursor-pointer"
                  />
                </label>
              </div>
            </div>

          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between border-t border-white/5 pt-4 shrink-0">
            <button
              type="button"
              onClick={handleResetDefaults}
              className="text-[11px] font-bold text-slate-500 hover:text-slate-300 transition-colors uppercase tracking-wider flex items-center space-x-1.5 px-2 py-1"
            >
              <i className="fas fa-undo text-[9px]"></i>
              <span>Restaurar Padrões</span>
            </button>

            <button
              type="button"
              onClick={onClose}
              className="bg-purple-600 hover:bg-purple-500 text-white font-black px-6 py-2.5 rounded-xl text-xs uppercase tracking-widest transition-all shadow-lg shadow-purple-600/25 active:scale-95 flex items-center space-x-2"
            >
              <i className="fas fa-check text-xs"></i>
              <span>Concluir</span>
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
